import React, { useEffect, useState, useMemo } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import NavbarVerticalMenu from "./NavbarVerticalMenu";
import routes from "routes/siteMaps";
import { useRoutePermissions } from "modules/user_management/store/permissionsStore";
import { buildSidebarRoutes } from "config/routeTabConfig";
import secureLocalStorage from "react-secure-storage";
import logo from "assets/img/illustrations/starfleet.png";
import { homeRoute } from "modules/utilities/utility";
import useLayoutMode from "hooks/useLayoutMode";
import { applyTheme } from "modules/navbar/top/ProfileDropdown";
import "./Sidebar.css";

const NavbarVertical = () => {
  const { fetchRoutePermissions, routePermissions, routeOrder, hasApiPermissions } = useRoutePermissions();
  const [panelOpen, setPanelOpen] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const uid = secureLocalStorage.getItem("id");
        if (uid) await fetchRoutePermissions(uid);
      } catch (_) {}
    };
    load();
  }, [fetchRoutePermissions]);

  // Apply saved theme on mount
  useEffect(() => {
    let savedTheme = secureLocalStorage.getItem("theme") || "light";
    // secureLocalStorage may return a JSON string for object themes — parse it
    if (typeof savedTheme === 'string' && savedTheme.startsWith('{')) {
      try { savedTheme = JSON.parse(savedTheme); } catch (_) {}
    }
    applyTheme(savedTheme);
  }, []);

  // Add sidebar class to html for content margin offset
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add("has-sidebar");
    return () => html.classList.remove("has-sidebar");
  }, []);

  // Toggle rail-only class on html when panel visibility changes
  useEffect(() => {
    const html = document.documentElement;
    if (panelOpen) {
      html.classList.remove("sidebar-rail-only");
    } else {
      html.classList.add("sidebar-rail-only");
    }
  }, [panelOpen]);

  // Build sidebar routes:
  // - If real RBAC permissions exist in DB → use buildSidebarRoutes (RBAC-filtered)
  // - Otherwise → fall back to role-based siteMaps (each role sees different menus)
  const allRoutes = useMemo(
    () => {
      if (hasApiPermissions) {
        const built = buildSidebarRoutes(routePermissions, routeOrder?.groupOrder);
        if (built.length > 0) return built;
      }
      // Fallback to role-based siteMaps (filtered by getCurrentUserRole())
      const fallback = routes.reduce((acc, group) => acc.concat(group.children || []), []);
      return fallback;
    },
    [routePermissions, routeOrder, hasApiPermissions]
  );

  const layoutMode = useLayoutMode();
  if (layoutMode === 'minimal') return null;

  return (
    <aside className={classNames("sidebar", { "sidebar--rail-only": !panelOpen })}>
      <div className="sidebar__brand sidebar__brand--glass">
        <Link to={homeRoute()} className="text-decoration-none d-flex align-items-center">
          {panelOpen ? (
            <img src={logo} alt="Logo" width={110} />
          ) : (
            <span className="sidebar__brand-icon">T</span>
          )}
        </Link>
      </div>
      <NavbarVerticalMenu routes={allRoutes} onPanelChange={setPanelOpen} />
    </aside>
  );
};

export default NavbarVertical;
